// @ts-strict-ignore
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import { Footer } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Tabs from 'antd/lib/tabs';
import { pick } from 'lodash';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import { ReleaseNotesInput } from '../metadata/ReleaseNotesInput';
import { antGrey } from '../styles/globalStyles';
import styles from '../styles/stepStyles';
import Text from 'antd/lib/typography/Text';
import { RightSquareOutlined } from '@ant-design/icons';
import { metadataRequirementsLinkForPlatform } from '../../helpers/displayHelpers';
import { useForm } from 'antd/lib/form/Form';
import { tryUpdateAppSettings, UpdateDefaultReleaseNotes } from '../../actions/appSettings';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { selectApp, selectAppPlatform, selectAppStoreType } from '../../selectors/selectors';
import Form from 'antd/lib/form';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface DefaultReleaseNotes {
    locale: string;
    whatsNew: string;
}

const SetReleaseNoteDefaultsComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const dispatch = useDispatch();

    const [form] = useForm();

    const allReleaseNotesUpdated = useUpdated(AppSettingsUpdatable.UpdateAppStoreDefaultReleaseNotes);
    const allReleaseNotesUpdateIsLoading = useIsLoading(AppSettingsUpdatable.UpdateAppStoreDefaultReleaseNotes);
    const allReleaseNotesUpdateError = useError(AppSettingsUpdatable.UpdateAppStoreDefaultReleaseNotes);

    const appStoreDefaultReleaseNotes = useSelector((state: RootState) => {
        const app = selectApp(state, props.appId);

        return app.appSettings.appStoreDefaultReleaseNotes.filter((releaseNotes) => {
            return releaseNotes.locale;
        });
    });

    const platform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const onClickSaveAllLocalizations = () => {
        const defaultReleaseNotes = form.getFieldValue('localizations') as DefaultReleaseNotes[];

        const request: UpdateDefaultReleaseNotes = {
            orgId: props.orgId,
            appId: props.appId,
            defaultReleaseNotes,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateAppStoreDefaultReleaseNotes, request));
    };

    const applyChangesToAllLocales = (i: number) => {
        let formData = form.getFieldValue(['localizations', i]);
        formData = pick(formData, ['whatsNew']);

        const newLocalizationsFormData = appStoreDefaultReleaseNotes.map((data, j) => {
            const oldData = form.getFieldValue(['localizations', j]);

            return {
                ...oldData,
                ...formData,
                id: data.id,
            };
        });

        form.setFieldValue('localizations', newLocalizationsFormData);

        globalMessage.success(`Copied notes from ${appStoreDefaultReleaseNotes[i].locale} to all locales!`);
    };

    const hasReleaseNoteLocalizations = appStoreDefaultReleaseNotes.length > 0;

    React.useEffect(() => {
        if (allReleaseNotesUpdated) {
            globalMessage.success('Default release notes saved!');
        }
    }, [allReleaseNotesUpdated]);

    React.useEffect(() => {
        if (allReleaseNotesUpdateError) {
            globalMessage.error(allReleaseNotesUpdateError);
        }
    }, [allReleaseNotesUpdateError]);

    return (
        <Layout>
            <Form
                form={form}
                initialValues={{
                    localizations: appStoreDefaultReleaseNotes,
                }}
                style={{ backgroundColor: antGrey.grey2, paddingLeft: 24, paddingRight: 24 }}
            >
                <Form.List name="localizations">
                    {(fields) => (
                        <Tabs
                            tabBarExtraContent={
                                <Space size="small">
                                    <Button
                                        type="link"
                                        icon={<RightSquareOutlined />}
                                        target="_blank"
                                        href={metadataRequirementsLinkForPlatform(platform)}
                                    >
                                        Metadata requirements
                                    </Button>
                                </Space>
                            }
                        >
                            {fields.map((fieldData, i) => {
                                const releaseNotes = appStoreDefaultReleaseNotes[i];

                                const localization = {
                                    id: releaseNotes.id,
                                    locale: releaseNotes.locale,
                                    displayLocale: releaseNotes.displayLocale,
                                    initialValue: releaseNotes.whatsNew,
                                };
                                return (
                                    <Tabs.TabPane
                                        tab={<Space>{releaseNotes.displayLocale}</Space>}
                                        key={releaseNotes.id}
                                    >
                                        <ReleaseNotesInput
                                            {...localization}
                                            shouldShowTitle
                                            appStoreType={appStoreType}
                                            fieldData={fieldData}
                                            shouldHideSetReleaseNotesDropdown
                                            saveDisabled={false}
                                            // allowsPatternedString={true} // todo: adjust once we implement templating for defaults, also pass availableTokens and disallowedTokens
                                            onApplyChangesToAllLocales={() => {
                                                if (appStoreDefaultReleaseNotes.length > 1) {
                                                    applyChangesToAllLocales(i);
                                                }
                                            }}
                                        />
                                    </Tabs.TabPane>
                                );
                            })}
                            {!hasReleaseNoteLocalizations && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexGrow: 1,
                                    }}
                                >
                                    <Text type="secondary">No release note locales found.</Text>
                                </div>
                            )}
                        </Tabs>
                    )}
                </Form.List>
            </Form>
            <Footer style={{ ...styles.footerStyle }}>
                <Button
                    onClick={onClickSaveAllLocalizations}
                    loading={allReleaseNotesUpdateIsLoading}
                    disabled={!hasReleaseNoteLocalizations}
                >
                    Save all changes
                </Button>
            </Footer>
        </Layout>
    );
};

export default SetReleaseNoteDefaultsComponent;
